import React, { useRef, useMemo, useEffect, CSSProperties } from 'react';
import { javascript } from '@codemirror/lang-javascript';
import { EditorState, StateEffect, Extension } from '@codemirror/state';
import {
  keymap,
  highlightSpecialChars,
  drawSelection,
  highlightActiveLine,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  lineNumbers,
  highlightActiveLineGutter,
  EditorView,
} from '@codemirror/view';
import {
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldGutter,
  foldKeymap,
} from '@codemirror/language';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
} from '@codemirror/autocomplete';
import { lintKeymap } from '@codemirror/lint';
import { json } from '@codemirror/lang-json';
import { sql } from '@codemirror/lang-sql';
import { python } from '@codemirror/lang-python';

// hack to allow string or editor doc
export interface EditorDoc {
  toString(): string;
  readonly length: number;
}

interface CodeMirrorProps {
  language?: 'javascript' | 'typescript' | 'json' | 'sql' | 'python';
  value: EditorDoc;
  onUpdateValue?: (value: EditorDoc) => void;
  readOnly?: boolean;
  lineWrapping?: boolean;
  className?: string;
  style?: CSSProperties;
}

const CodeMirror: React.FC<CodeMirrorProps> = props => {
  const { language, value, onUpdateValue, readOnly, lineWrapping, className, style } = props;

  const root = useRef<HTMLDivElement>(null);

  const editorView = useRef<EditorView>();

  const extensions = useMemo(() => {
    const extensions: Extension[] = [basicSetup];
    switch (language) {
      case 'json':
        extensions.push(json());
        break;
      case 'sql':
        extensions.push(
          sql({
            upperCaseKeywords: true,
          }),
        );
        break;
      case 'javascript':
      case 'typescript':
        extensions.push(javascript({ typescript: language === 'typescript' }));
        break;
      case 'python':
        extensions.push(python());
        break;
    }

    if (readOnly) {
      extensions.push(EditorView.editable.of(false));
    }

    if (lineWrapping) {
      extensions.push(EditorView.lineWrapping);
    }

    if (onUpdateValue) {
      const updateListener = EditorView.updateListener.of(viewUpdate => {
        if (viewUpdate.docChanged && viewUpdate.view.hasFocus) {
          onUpdateValue(viewUpdate.state.doc);
        }
      });
      extensions.push(updateListener);
    }

    const theme = EditorView.theme({
      '&.cm-editor.cm-focused': {
        outline: 'none',
      },
    });
    extensions.push(theme);

    return extensions;
  }, [language, onUpdateValue, readOnly, lineWrapping]);

  useEffect(() => {
    if (editorView.current) {
      editorView.current.dispatch({
        effects: StateEffect.reconfigure.of(extensions),
      });
    }
  }, [extensions]);

  useEffect(() => {
    if (editorView.current) {
      const view = editorView.current;
      if (value !== view.state.doc) {
        view.dispatch({
          changes: {
            from: 0,
            to: view.state.doc.length,
            insert: value.toString(),
          },
        });
      }
    }
  }, [value]);

  useEffect(() => {
    if (root.current) {
      const state = EditorState.create({
        doc: value.toString(),
        extensions,
      });
      editorView.current = new EditorView({
        state,
        parent: root.current,
      });
    }
    return () => {
      if (editorView.current) {
        editorView.current.destroy();
        editorView.current = undefined;
      }
    };
  }, []);

  return <div className={className} style={style} ref={root} />;
};

CodeMirror.displayName = 'CodeMirror';

export default CodeMirror;

// Copied from https://github.com/codemirror/basic-setup/blob/main/src/codemirror.ts
const basicSetup: Extension = (() => [
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  dropCursor(),
  EditorState.allowMultipleSelections.of(true),
  indentOnInput(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  rectangularSelection(),
  crosshairCursor(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap,
  ]),
])();
